import React, { useState, useEffect } from 'react';
import { getDetalleEstudianteInscripcion, aplicarInscripcion } from '../../api/inscripcionReinscripcion';
import styles from '../../styles/InscripcionReinscripcionStyles/InscripcionReinscripcion.module.css';

function StudentDetail({ student, onBack, searchFilters, searchTerm, onSearch }) {
  const [showPagosRealizados, setShowPagosRealizados] = useState(true);
  const [showPagosPendientes, setShowPagosPendientes] = useState(true);
  const [detalleEstudiante, setDetalleEstudiante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aplicandoInscripcion, setAplicandoInscripcion] = useState(false);

  useEffect(() => {
    cargarDetalleEstudiante();
  }, [student.id_estudiante]); // Cambiar student.id a student.id_estudiante

  const cargarDetalleEstudiante = async () => {
    try {
      setLoading(true);
      setError(null);
      const detalle = await getDetalleEstudianteInscripcion(student.id_estudiante); // Cambiar student.id a student.id_estudiante
      setDetalleEstudiante(detalle);
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      setError('Error al cargar información del estudiante');
    } finally {
      setLoading(false);
    }
  };

  const handleAplicarInscripcion = async () => {
    if (!detalleEstudiante) return;

    // Validar programa
    if (!student.id_programa_actual || typeof student.id_programa_actual !== 'number') {
      alert('No se puede aplicar inscripción: el estudiante no tiene programa asignado.');
      return;
    }

    // Validar ciclo escolar
    if (!searchFilters.cicloEscolar) {
      alert('No se puede aplicar inscripción: no se ha seleccionado un ciclo escolar válido.');
      return;
    }

    const cicloSigNombre = student.ciclo_siguiente || searchFilters.cicloSiguienteNombre || 'siguiente ciclo';
    const tipoPeriodoProg = student.tipo_periodo?.nombre || '';
    const tipoPeriodoCiclo = student.tipo_periodo_ciclo || '';
    
    let mensajeConfirmacion = `¿Está seguro de aplicar la reinscripción para ${detalleEstudiante.nombre} al ciclo ${cicloSigNombre}?`;
    
    // Mostrar info de tipo de periodo en la confirmación
    if (tipoPeriodoProg) {
      mensajeConfirmacion += `\n\nTipo de periodo del programa: ${tipoPeriodoProg}`;
      if (tipoPeriodoCiclo) {
        mensajeConfirmacion += `\nTipo de periodo del ciclo: ${tipoPeriodoCiclo}`;
      }
    }
    
    // Advertir si hay incompatibilidad
    if (!student.ciclo_compatible && student.advertencias_ciclo?.length > 0) {
      mensajeConfirmacion += `\n\n⚠️ ADVERTENCIA: ${student.advertencias_ciclo.join(' ')}`;
    }
    
    const confirmacion = window.confirm(mensajeConfirmacion);

    if (!confirmacion) return;

    try {
      setAplicandoInscripcion(true);

      // Enviar ciclo destino explícito para evitar ambigüedad de detección
      const inscripcionData = {
        id_estudiante: student.id_estudiante,
        id_ciclo_destino: student.ciclo_siguiente_id || searchFilters.cicloSiguienteId,
        // Se conserva como referencia opcional para validaciones de secuencia en backend
        id_ciclo_escolar: student.ciclo_escolar_id || null,
        id_programa: student.id_programa_actual,
        tipo_inscripcion: 'REINSCRIPCION'
      };

      const response = await aplicarInscripcion(inscripcionData);

      let mensaje = 'Inscripción aplicada exitosamente.';
      if (response.inscripcion?.ciclo_inscripcion) {
        mensaje += `\nCiclo de inscripción: ${response.inscripcion.ciclo_inscripcion}`;
      }
      // Mostrar info de detección de ciclo
      if (response.deteccion_ciclo) {
        const det = response.deteccion_ciclo;
        if (det.tipo_periodo_programa) {
          mensaje += `\nTipo periodo programa: ${det.tipo_periodo_programa}`;
        }
        if (det.tipo_periodo_ciclo) {
          mensaje += `\nTipo periodo ciclo: ${det.tipo_periodo_ciclo}`;
        }
        if (det.compatible) {
          mensaje += `\n✅ Tipo de periodo compatible`;
        }
      }
      if (response.advertencias?.length > 0) {
        mensaje += `\n\n⚠️ Advertencias:\n${response.advertencias.join('\n')}`;
      }
      if (response.cargo_registro) {
        mensaje += `\n\nCargo generado para ciclo: ${response.cargo_registro.ciclo_cargo || 'N/A'}\n• Concepto: ${response.cargo_registro.concepto}`;
        if (response.cargo_registro.aplico_descuento) {
          mensaje += `\n• Monto original: $${response.cargo_registro.monto_original.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
          mensaje += `\n• Descuento pronto pago: -$${response.cargo_registro.descuento_pronto_pago.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
          mensaje += `\n• Monto final: $${response.cargo_registro.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
        } else {
          mensaje += `\n• Monto: $${response.cargo_registro.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
        }
      } else if (response.cargo_duplicado) {
        mensaje += '\n\n⚠️ El cargo de registro ya existía previamente.';
      }

      try {
        window.dispatchEvent(new CustomEvent('inscripciones:updated', {
          detail: {
            action: 'reinscripcion_aplicada',
            id_estudiante: student.id_estudiante,
            id_ciclo_escolar: inscripcionData.id_ciclo_destino
          }
        }));
      } catch (_) {}

      alert(`✅ ${mensaje}`);
      onBack();
    } catch (err) {
      console.error('Error al aplicar inscripción:', err);
      const mensaje = err.message || 'Error al aplicar inscripción';
      alert(`Error: ${mensaje}`);
    } finally {
      setAplicandoInscripcion(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.studentDetailContainer}>
        <div className={styles.studentBanner}>
          <span className={styles.bannerText}>Cargando información...</span>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          Cargando datos del estudiante...
        </div>
      </div>
    );
  }

  if (error || !detalleEstudiante) {
    return (
      <div className={styles.studentDetailContainer}>
        <div className={styles.studentBanner}>
          <span className={styles.bannerText}>Error</span>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
          {error || 'No se pudo cargar la información del estudiante'}
        </div>
        <button onClick={onBack} style={{ margin: '20px auto', display: 'block' }}>
          Volver
        </button>
      </div>
    );
  }

  const pagosRealizados = detalleEstudiante.pagos_realizados || [];
  const pagosPendientes = detalleEstudiante.pagos_pendientes || [];
  const totalPagado = detalleEstudiante.estado_financiero?.total_pagado || 0;
  const totalPendiente = detalleEstudiante.estado_financiero?.total_pendiente || 0;
  const bloqueado = detalleEstudiante.estado_financiero?.bloqueado || false;
  const tieneCargos = detalleEstudiante.tiene_cargos || false;

  return (
    <div className={styles.studentDetailContainer}>
      {/* Banner superior azul */}
      <div className={styles.studentBanner}>
        <span className={styles.bannerText}>Estudiante: {detalleEstudiante.nombre}</span>
      </div>

      {/* Mensaje si no tiene cargos */}
      {!tieneCargos && (
        <div className={styles.alertCard} style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
          <div className={styles.alertIcon}>ℹ️</div>
          <span className={styles.alertText}>El alumno aún no cuenta con cargos asignados</span>
          <span className={styles.alertSubtext}>
            No se han registrado conceptos de pago para este estudiante
          </span>
        </div>
      )}

      {/* Card de información del estudiante */}
      <div className={styles.studentInfoCard}>
        <div className={styles.studentAvatar}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        </div>
        
        <div className={styles.studentInfoGrid}>
          <div className={styles.infoRow}>
            <div className={styles.infoField}>
              <label>Matrícula</label>
              <span>{detalleEstudiante.matricula}</span>
            </div>
            <div className={styles.infoField}>
              <label>Institución</label>
              <span>{detalleEstudiante.institucion}</span>
            </div>
          </div>
          
          <div className={styles.infoRow}>
            <div className={styles.infoField}>
              <label>Programa</label>
              <select className={styles.programSelect} disabled>
                <option>{detalleEstudiante.programa}</option>
              </select>
            </div>
            <div className={styles.infoField}>
              <label>Campus</label>
              <span>{detalleEstudiante.campus}</span>
            </div>
          </div>
          
          <div className={styles.infoRow}>
            <div className={styles.infoField}>
              <label>Periodo correspondiente</label>
              <span>{detalleEstudiante.periodo_actual}</span>
            </div>
          </div>
          
          <div className={styles.infoRow}>
            <div className={styles.infoField}>
              <label>Estado financiero</label>
              <div className={styles.financialStatus}>
                {bloqueado ? (
                  <>
                    <span className={styles.blockedIcon}>🚫</span>
                    <span className={styles.blockedText}>Bloqueado por adeudos</span>
                  </>
                ) : (
                  <>
                    <span className={styles.blockedIcon}>✅</span>
                    <span className={styles.blockedText} style={{ color: '#28a745' }}>Al corriente</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.financialAmounts}>
            <div className={styles.amountItem}>
              <span className={styles.amountLabel}>Pagado:</span>
              <span className={styles.amountValue}>${totalPagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className={styles.amountItem}>
              <span className={styles.amountLabel}>Pendiente:</span>
              <span className={styles.amountValue}>${totalPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pagos Realizados */}
      {pagosRealizados.length > 0 && (
        <div className={styles.paymentsCard}>
          <div className={styles.cardHeader} onClick={() => setShowPagosRealizados(!showPagosRealizados)}>
            <div className={styles.cardTitleContainer}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2-7h-3V2h-2v2H8V2H6v2H3v2h18V4z"/>
                </svg>
              </div>
              <span className={styles.cardTitle}>Pagos Realizados</span>
            </div>
            <div className={styles.cardActions}>
              <span className={styles.totalAmount}>Total pagado: ${totalPagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              <span className={styles.expandIcon}>{showPagosRealizados ? '▼' : '▶'}</span>
            </div>
          </div>
          
          {showPagosRealizados && (
            <div className={styles.cardContent}>
              <table className={styles.paymentsTable}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Método</th>
                    <th>Monto</th>
                    <th>Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {pagosRealizados.map((pago, index) => (
                    <tr key={index}>
                      <td>{pago.fecha}</td>
                      <td>{pago.concepto}</td>
                      <td>{pago.metodo}</td>
                      <td>${pago.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td>{pago.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Alerta de bloqueo */}
      {bloqueado && pagosPendientes.length > 0 && (
        <div className={styles.alertCard}>
          <div className={styles.alertIcon}>⚠️</div>
          <span className={styles.alertText}>Bloqueado por adeudos</span>
          <span className={styles.alertSubtext}>
            Debe realizar los pagos correspondientes antes de la fecha límite
          </span>
        </div>
      )}

      {/* Pagos Pendientes */}
      {pagosPendientes.length > 0 && (
        <div className={styles.paymentsCard}>
          <div className={styles.cardHeader} onClick={() => setShowPagosPendientes(!showPagosPendientes)}>
            <div className={styles.cardTitleContainer}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <span className={styles.cardTitle}>Pagos Pendientes</span>
            </div>
            <div className={styles.cardActions}>
              <span className={styles.totalAmount}>Total pendiente: ${totalPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              <span className={styles.expandIcon}>{showPagosPendientes ? '▼' : '▶'}</span>
            </div>
          </div>
          
          {showPagosPendientes && (
            <div className={styles.cardContent}>
              <table className={styles.paymentsTable}>
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Fecha Límite</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {pagosPendientes.map((pago, index) => (
                    <tr key={index}>
                      <td>{pago.concepto}</td>
                      <td>{pago.fecha_limite}</td>
                      <td>${pago.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className={styles.cardFooter}>
                <button className={styles.downloadButton}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7,10 12,15 17,10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Descargar estado de cuenta
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Botón principal */}
      <div className={styles.actionButtonContainer}>
        <button 
          className={styles.primaryActionButton}
          onClick={handleAplicarInscripcion}
          disabled={aplicandoInscripcion}
        >
          {aplicandoInscripcion ? 'PROCESANDO...' : 'APLICAR INSCRIPCIÓN Y ENVIAR CONFIRMACIÓN'}
        </button>
      </div>
    </div>
  );
}

export default StudentDetail;
